import React from "react";
import { Col, Row, Form, FloatingLabel } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { TextInput } from "./Inputs";

// Component reutilitzable de camps d'adreca.
// S'utilitza en formularis on cal editar o visualitzar una adreça
// amb selectors de pais -> provincia -> municipi -> poblacio.
export const AddressFields = ({
  control,
  errors,
  paises,
  provincias,
  municipios,
  poblaciones,
  tipovia,
  loadingProv,
  loadingMuni,
  loadingPob,
  idPais,
  idProv,
  idMuni,
  onPaisChange,
  onProvinciaChange,
  onMunicipioChange,
  watchCountry = "cliente_id_pais",
  watchProvince = "cliente_id_provincia",
  watchMunicipio = "cliente_id_municipi",
  watchPoblacion = "cliente_id_poblacio",
  prefix = "cliente_",
  noFloating = false,
  readOnly = false,
  loadError = null,
  prefillLabels = {},
}) => {
  // readOnly pot ser un booleà (aplica a tot el bloc) o una funció (fieldName) => booleà
  // per bloquejar nomes els camps que ja venen precarregats amb valor.
  const resolveReadOnly = (fieldName) =>
    typeof readOnly === "function" ? readOnly(fieldName) : readOnly;

  const renderSelect = (field, label, options, loading, disabled, error, prefillLabel) => {
    // Determine if we should treat it as visually disabled (read-only) vs functionally disabled (loading/locked)
    // If passed 'disabled' is true, check if it's because of readOnly or logic.
    // We want to SHOW the value if it's just readOnly.
    // Strategy: If readOnly is passed to component (not this func), we use pointer-events.
    // But 'disabled' arg here mixes readOnly || logic.
    // Let's rely on the passed 'disabled' arg, but if it's TRUE, we check if we have a value.
    // If we have a value and options, we might want to show it.

    // Better approach: User passes 'readOnly' to AddressFields.
    // renderSelect receives 'disabled' which is (readOnly || logic).
    // If it is disabled, we apply styles. But we avoid the 'disabled' attribute on the select IF we have a value, so it shows up.
    // BUT HTML Select with 'disabled' prevents submission? No, using react-hook-form Controller handles submission via state, not DOM submission usually.
    // However, keeping it enabled matches 'readonly' semantics better for text inputs.

    const isVisuallyDisabled = disabled;

    // Normalitza el valor a string per evitar inconsistencies entre ids numerics i strings.
    const normalizedValue =
      field.value === null || field.value === undefined || field.value === ""
        ? ""
        : String(field.value);

    const select = (
      <Form.Select
        {...field}
        value={normalizedValue}
        onChange={(e) => {
          field.onChange(e);
          // Quan canvia un select pare, executa la carrega del seguent nivell.
          if (label === "País" && onPaisChange) onPaisChange(e.target.value);
          if (label === "Provincia" && onProvinciaChange) onProvinciaChange(e.target.value);
          if (label === "Municipio" && onMunicipioChange) onMunicipioChange(e.target.value);
        }}
        // Only effectively disable interaction if loading or truly disabled logic, 
        // but for readOnly we use CSS to block interaction so value remains visible.
        disabled={loading}
        className={`rounded-3 bg-white shadow-sm ${error ? "is-invalid" : ""}`}
        size="md"
        style={{
          opacity: isVisuallyDisabled ? 0.8 : 1,
          backgroundColor: isVisuallyDisabled ? "#e9ecef" : "#fff",
          pointerEvents: isVisuallyDisabled ? "none" : "auto",
          ...(isVisuallyDisabled ? { appearance: "none" } : {}),
        }}
        tabIndex={isVisuallyDisabled ? -1 : 0}
        aria-disabled={isVisuallyDisabled}
      >
        <option value="" disabled>Seleccione {label.toLowerCase()}</option>
        {loading ? (
          <option>Cargando {label.toLowerCase()}s...</option>
        ) : (
          <>
            {options?.map((opt) => (
              <option key={opt.id} value={String(opt.id)}>
                {opt.name}
              </option>
            ))}
            {/* Si el valor actual ve carregat per l'ERP pero encara no existeix a les opcions carregades,
                es manté visible per no perdre el context a la UI. */}
            {normalizedValue && (!options || !options.find(o => String(o.id) === normalizedValue)) && (
              <option value={normalizedValue}>
                {prefillLabel || normalizedValue}
              </option>
            )}
          </>
        )}
      </Form.Select>
    );

    return (
      <div className="mb-2">
        {!noFloating ? (
          <FloatingLabel controlId={`floating${field.name}`} label={label}>
            {select}
          </FloatingLabel>
        ) : (
          select
        )}
        {error && <div className="text-danger small mt-1 fw-bold">{error}</div>}
      </div>
    );
  };

  return (
    // Bloc complet de formulari d'adreca: combina selects dependents i inputs lliures.
    <div className="bg-light p-4 rounded-4 border border-light mb-4">
      <Row className="mb-4">
        <Col className="text-start">
          <h5 className="fw-bold text-secondary mb-0">Dirección</h5>
          {loadError && (
            <div className="alert alert-warning mt-2 mb-0 py-2 smaller shadow-sm border-warning">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {loadError}
            </div>
          )}
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={6}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">País</label>}
          <Controller
            name={watchCountry}
            control={control}
            render={({ field, fieldState: { error } }) =>
              renderSelect(field, "País", paises, false, resolveReadOnly(watchCountry), error?.message, prefillLabels.pais)
            }
          />
        </Col>
        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1">
              {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Provincia</label>}
              <Controller
                name={watchProvince}
                control={control}
                render={({ field, fieldState: { error } }) =>
                  renderSelect(
                    field,
                    "Provincia",
                    provincias,
                    loadingProv,
                    resolveReadOnly(watchProvince) || !idPais || loadingProv || !provincias?.length,
                    error?.message,
                    prefillLabels.provincia
                  )
                }
              />
            </div>
            {loadingProv && (
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1">
              {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Municipio</label>}
              <Controller
                name={watchMunicipio}
                control={control}
                render={({ field, fieldState: { error } }) =>
                  renderSelect(
                    field,
                    "Municipio",
                    municipios,
                    loadingMuni,
                    resolveReadOnly(watchMunicipio) || !idProv || loadingMuni || !municipios?.length,
                    error?.message,
                    prefillLabels.municipio
                  )
                }
              />
            </div>
            {loadingMuni && (
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
            )}
          </div>
        </Col>

        <Col md={6}>
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1">
              {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Población</label>}
              <Controller
                name={watchPoblacion}
                control={control}
                render={({ field, fieldState: { error } }) =>
                  renderSelect(
                    field,
                    "Población",
                    poblaciones,
                    loadingPob,
                    resolveReadOnly(watchPoblacion) || !parseInt(idMuni) || loadingPob || !poblaciones?.length,
                    error?.message,
                    prefillLabels.poblacion
                  )
                }
              />
            </div>
            {loadingPob && (
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={3} md={4} xs={12}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Tipo de vía</label>}
          <Controller
            name={`${prefix}tv`}
            control={control}
            render={({ field, fieldState: { error } }) =>
              renderSelect(field, "Tipo de vía", tipovia, false, resolveReadOnly(`${prefix}tv`), error?.message)
            }
          />
        </Col>
        <Col lg={6} md={8} xs={12}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Calle</label>}
          <Controller
            name={`${prefix}nv`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Calle"
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}nv`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
        <Col lg={3} md={12}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Número</label>}
          <Controller
            name={`${prefix}pnp`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Número"
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}pnp`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={3} md={6} xs={6}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Escalera</label>}
          <Controller
            name={`${prefix}es`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Escalera"
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}es`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
        <Col lg={3} md={6} xs={6}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Planta</label>}
          <Controller
            name={`${prefix}pt`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Planta"
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}pt`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
        <Col lg={3} md={6} xs={6}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Puerta</label>}
          <Controller
            name={`${prefix}pu`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Puerta"
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}pu`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
        <Col lg={3} md={6} xs={6}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">C.P.</label>}
          <Controller
            name={`${prefix}dp`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="C.P."
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}dp`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
      </Row>
      <Row className="g-3 align-items-center">
        <Col md={12}>
          {noFloating && <label className="text-muted small fw-bold text-uppercase mb-2">Aclarador</label>}
          <Controller
            name={`${prefix}aclarador`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Aclarador"
                noFloating={noFloating}
                error={error?.message}
                readOnly={resolveReadOnly(`${prefix}aclarador`)}
                className="bg-white shadow-sm"
              />
            )}
          />
        </Col>
      </Row>
    </div>
  );
};